import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import Styles from "./FormInput.module.css";

const errorMsgSpacing = "25px";

const ErrorMessage = ({ error, fieldName, show }) => {
  return (
    <div
      style={{
        minHeight: errorMsgSpacing,
        display: "flex",
        alignItems: "center",
      }}
    >
      {error && error[fieldName] && show && (
        <div className={`${Styles.errorText} ${Styles.shake}`}>{error[fieldName]}</div>
      )}
    </div>
  );
};

const columnStyle = (columnSize) => {
  switch (columnSize) {
    case "sm":
    case "small":
      return "col-12 col-md-3";
    case "md":
    case "medium":
      return "col-12 col-md-4";
    case "lg":
    case "large":
      return "col-12 col-md-8";
    default:
      return "col-12 col-md-6";
  }
};

const FormHeader = ({ children }) => {
  return <h5 className="text-primary fw-bold text-center mb-4">{children}</h5>;
};

const TextInput = ({
  error,
  fieldName,
  value,
  handleInputChange,
  placeholder = "",
  type = "text",
  display = true,
  columnSize,
  parentField,
  showErrorMessage = true,
  props,
  id = "",
  autoComplete
}) => {
  const [isDirty, setIsDirty] = useState(false);
  const [onBlur, setOnBlur] = useState(false);

  useEffect(() => {
    setIsDirty(false)
    setOnBlur(false)
  }, [error])

  const onChange = (e) => {
    handleInputChange(fieldName, e.target.value, parentField) 
    setIsDirty(true)
  }

  if (!display) {
    return null;
  }

  return (
    <>
      <div className={columnStyle(columnSize)}>
        <input
          id={id+fieldName}
          type={type}
          className={`form-control ${Styles.inputText}`}
          value={value || ""}
          onChange={onChange}
          placeholder={placeholder}
          onBlur={() => setOnBlur(true)}
          autoComplete={autoComplete}
          {...props}
        />

        {showErrorMessage && <ErrorMessage error={error} fieldName={fieldName} show={!isDirty || !onBlur}/>}
      </div>
    </>
  );
};

const RadioInput = ({
  error,
  fieldName,
  value,
  handleInputChange,
  display = true,
  data,
  options = [],
  sameLine = false,
  sameLineAsHeader = false,
  parentField,
  showErrorMessage = true,
  id = "",
}) => {
  const [isDirty, setIsDirty] = useState(false);
  const [onBlur, setOnBlur] = useState(false);

  useEffect(() => {
    setIsDirty(false)
    setOnBlur(false)
  }, [error])

  const onChange = (e) => {
    if(data && typeof Number(e.target.value) == "number"){
      handleInputChange(fieldName, data[e.target.value], parentField)
    }else{
      handleInputChange(fieldName, e.target.value, parentField)
    }
    setIsDirty(true)
  }

  if (!display) {
    return null;
  }

  return (
    <>
      <div
        className={sameLineAsHeader ? columnStyle() : ""}
        style={{
          display: "flex",
          flexDirection: sameLine ? "row" : "column",
          gap: "10px",
          margin: "10px 0 ",
        }}
      >
        {options.map((option, index) => (
          <div key={index} className="form-check form-check-inline">
            <label className="form-check-label">
              <input
                id={id+fieldName}
                type="radio"
                className={`form-check-input ${Styles.inputText}`}
                name={fieldName + id}
                value={option.value || ""}
                checked={value === option.value}
                onChange={onChange}
                onBlur={() => setOnBlur(true)}
              />
              {option.label}
            </label>
          </div>
        ))}
      </div>
      {showErrorMessage && <ErrorMessage error={error} fieldName={fieldName} show={!isDirty || !onBlur}/>}
    </>
  );
};

const SelectInput = ({
  id = "",
  error,
  fieldName,
  value,
  handleInputChange,
  display = true,
  options = [],
  sameLine = false,
  parentField,
  defaultText,
  showErrorMessage = true,
}) => {
  const [isDirty, setIsDirty] = useState(false);
  const [onBlur, setOnBlur] = useState(false);

  useEffect(() => {
    setIsDirty(false)
    setOnBlur(false)
  }, [error])

  const onChange = (e) => {
    handleInputChange(fieldName, e.target.value, parentField) 
    setIsDirty(true)
  }

  if (!display) {
    return null;
  }

  return (
    <div className={columnStyle()}>
      <select
        id={id+fieldName}
        className={`form-select ${Styles.inputText}`}
        name={fieldName}
        value={value || "none"}
        onChange={onChange}
        onBlur={() => setOnBlur(true)}
      >
        {defaultText && (
          <option value="none" disabled>
            {defaultText}
          </option>
        )}
        {options.map((option, index) => (
          <option key={index} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {showErrorMessage && <ErrorMessage error={error} fieldName={fieldName} show={!isDirty || !onBlur}/>}
    </div>
  );
};

const DateInput = ({
  id = "",
  props,
  error,
  addError,
  fieldName,
  value,
  handleInputChange,
  display = true,
  parentField,
  showErrorMessage = true,
  mode = "date",
  hideKeyboardOnMobile = true,
}) => {
  const [isDirty, setIsDirty] = useState(true);

  useEffect(() => {
    if(error[fieldName])
      setIsDirty(false)
  }, [error])

  const onChange = (date) => {
    handleInputChange(fieldName, date, parentField) 
    setIsDirty(true)
  }

  const onChangeRaw = (e) => {
    if(mode != "date" || typeof e.target.value != "string")
      return

    const digits = e.target.value.replace(/\D/g, "");
    let formatted = digits;
    if (digits.length >= 3 && digits.length <= 4) {
      formatted = digits.slice(0, 2) + "/" + digits.slice(2);
    } else if (digits.length >= 5) {
      formatted =
        digits.slice(0, 2) +
        "/" +
        digits.slice(2, 4) +
        "/" +
        digits.slice(4, 8);
    }
    e.target.value = formatted
    if(digits.length <= 8){
      setIsDirty(true)
    }
    if (/^\d{8}$/.test(digits)) {
      validateDate(formatted)
    }
  }

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const CustomInput = React.forwardRef(({ value, onClick }, ref) => (
    <button
      id={isMobile && id+fieldName}
      type="button"
      className={`form-control ${Styles.inputText}`}
      onFocus={onClick}
      onClick={onClick}
      ref={ref}
      style={{textAlign: "start", color: 'gray'}}
    >
      {value || props.placeholderText || "Select Date"}
    </button>
  ));

  const validateDate = (value) => {
    let isValid = true
    if(!value)
      return false

    const date = new Date(value)

    if(date instanceof Date && isNaN(date)){
      addError({[fieldName]: "Invalid Date"})
      isValid = false
    }

    if(addError != null) {
      if(!props?.allowFutureDate && date > Date.now()){
        addError({[fieldName]: "Cannot Enter Future Date"})
        isValid = false
      }
    }

    if (isValid) {
      handleInputChange(fieldName, date, parentField) 
    } else {
      handleInputChange(fieldName, null, parentField) 
    }

    return isValid
  }

  if (!display) {
    return null;
  }

  const CalendarContainer = ({className, children}) => {
    return (
      <div className={className} style={{display: isDirty ? 'show' : 'none'}}>
          {children}
      </div>
    )
  }

  return (
    <div className={columnStyle()}>
      <DatePicker
        id={!isMobile && id+fieldName}
        popperClassName={`react-datepicker-popper ${Styles.calendarPopper}`}
        selected={value || ""}
        onChangeRaw={onChangeRaw}
        onChange={onChange}
        className={`form-control ${Styles.inputText}`}
        customInput={hideKeyboardOnMobile && isMobile && <CustomInput />}
        calendarContainer={CalendarContainer}
        onBlur={e => validateDate(e.target.value)}
        {...props}
      />
      {showErrorMessage && <ErrorMessage error={error} fieldName={fieldName} show={!isDirty}/>}
    </div>
  );
};

const PhoneNumberInput = ({
  fieldName,
  value,
  handleInputChange,
  error,
  parentField,
  showErrorMessage = true,
  id = "",
  autoComplete
}) => {
  const [isDirty, setIsDirty] = useState(false);
  const [onBlur, setOnBlur] = useState(false);

  useEffect(() => {
    setIsDirty(false)
    setOnBlur(false)
  }, [error])

  // Function to format the phone number
  const formatPhoneNumber = (input) => {
    if(isNaN(parseInt(input)))
      return

    const numbers = input.replace(/\D/g, ""); // Remove all non-numeric characters
    const match = numbers.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);

    if (!match) return input;

    let formattedNumber = "";
    if (match[1]) formattedNumber += `(${match[1]}`;
    if (match[2]) formattedNumber += `) ${match[2]}`;
    if (match[3]) formattedNumber += ` - ${match[3]}`;

    return formattedNumber;
  };

  const handleKeyPress = (e) => {
    // Allow control keys like backspace, arrow keys, etc.
    if (e.key.length > 1) return;
    if (!/[0-9]/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleChange = (e) => {
    const unformattedNumber = e.target.value.replace(/\D/g, "");
    handleInputChange(fieldName, unformattedNumber, parentField);
    setIsDirty(true)
  };

  return (
    <div className={columnStyle()}>
      <input
        id={id+fieldName}
        type="tel"
        className={`form-control ${Styles.inputText}`}
        value={formatPhoneNumber(value)}
        onChange={handleChange}
        onKeyDown={handleKeyPress}
        placeholder="(___) ___-____"
        maxLength={16} // Restrict input length
        onBlur={() => setOnBlur(true)}
        autoComplete={autoComplete || fieldName}
      />
      {showErrorMessage && <ErrorMessage error={error} fieldName={fieldName} show={!isDirty || !onBlur}/>}
    </div>
  );
};

const TextField = ({
  id = "",
  error,
  fieldName,
  value,
  handleInputChange,
  placeholder = "",
  display = true,
  parentField,
  rows = 3,
  showErrorMessage = true,
}) => {
  const [isDirty, setIsDirty] = useState(false);
  const [onBlur, setOnBlur] = useState(false);

  useEffect(() => {
    setIsDirty(false)
    setOnBlur(false)
  }, [error])

  const onChange = (e) => {
    handleInputChange(fieldName, e.target.value, parentField) 
    setIsDirty(true)
  }

  if (!display) {
    return null;
  }

  return (
    <>
      <div className="col-12 mb-2">
        <textarea
          id={id+fieldName}
          className={`form-control ${Styles.inputText}`}
          name={fieldName}
          value={value || ""}
          onChange={onChange}
          placeholder={placeholder}
          rows={rows}
          onBlur={() => setOnBlur(true)}
        />
        {showErrorMessage && <ErrorMessage error={error} fieldName={fieldName} show={!isDirty || !onBlur}/>}
      </div>
    </>
  );
};

const CheckboxInput = ({
  id = "",
  error,
  fieldName,
  value = [],
  handleInputChange,
  display = true,
  options = [],
  parentField,
  showErrorMessage = true,
}) => {
  const [isDirty, setIsDirty] = useState(false);
  const [onBlur, setOnBlur] = useState(false);

  useEffect(() => {
    setIsDirty(false)
    setOnBlur(false)
  }, [error])

  if (!display) {
    return null;
  }

  const handleChange = (checkedValue) => {
    const newValue = value.includes(checkedValue)
      ? value.filter((v) => v !== checkedValue)
      : [...value, checkedValue];
    handleInputChange(fieldName, newValue, parentField);
    setIsDirty(true)
  };

  return (
    <div className="col-12 mb-2">
      {options.map((option, index) => (
        <div key={index} className="form-check" id={id+fieldName}>
          <input
            type="checkbox"
            className="form-check-input"
            id={`${fieldName}-${index}`}
            checked={value.includes(option.value)}
            onChange={() => handleChange(option.value)}
            onBlur={() => setOnBlur(true)}
          />
          <label className="form-check-label" htmlFor={`${fieldName}-${index}`}>
            {option.label}
          </label>
        </div>
      ))}
      {showErrorMessage && <ErrorMessage error={error} fieldName={fieldName} show={!isDirty || !onBlur}/>}
    </div>
  );
};

const AddressInput = ({
  addressData,
  handleInputChange,
  parentField,
  error,
  id,
  autoComplete,
  enableValidation = true,
  fieldNames
}) => {
  const streetAddressFieldName = fieldNames?.StreetAddress || "StreetAddress"
  const apartmentNoFieldName = fieldNames?.ApartmentNo || "ApartmentNo"
  const cityFieldName = fieldNames?.City || "City"
  const zipCodeFieldName = fieldNames?.ZipCode || "ZipCode"

  const handleAddressChange = async (fieldName, value) => {
    handleInputChange(fieldName, value, parentField);

    if (enableValidation && fieldName == zipCodeFieldName && value.toString().length == 5) {
      const state = await checkState(value);
      handleInputChange("State", state, parentField);
    }
  };

  const checkState = async (zip) => {
    try {
      const response = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (!response.ok) {
        throw Error("Invalid ZIP Code");
      }
      const data = await response.json();
      return data.places[0]["state abbreviation"] || "";
    } catch (error) {
      console.error("Error fetching ZIP code data:", error);
      return null;
    }
  };

  return (
    <>
      <TextInput
        fieldName={streetAddressFieldName}
        placeholder="Street Address"
        value={addressData?.[streetAddressFieldName]}
        handleInputChange={handleAddressChange}
        columnSize="lg"
        error={error}
        id={id}
        autoComplete="address-line1"
      />
      <TextInput
        fieldName={apartmentNoFieldName}
        placeholder="Apt#"
        value={addressData?.[apartmentNoFieldName]}
        handleInputChange={handleAddressChange}
        columnSize="md"
        error={error}
        id={id}
        autoComplete="address-line2"
      />
      <TextInput
        fieldName={cityFieldName}
        placeholder="City"
        value={addressData?.[cityFieldName]}
        handleInputChange={handleAddressChange}
        columnSize="lg"
        error={error}
        id={id}
        autoComplete="address-level2"
      />
      <TextInput
        fieldName={zipCodeFieldName}
        placeholder="Zip"
        value={addressData?.[zipCodeFieldName]}
        handleInputChange={handleAddressChange}
        columnSize="md"
        error={error}
        props={{
          inputMode: "decimal",
          pattern: "[0-9]*\.?[0-9]*",
        }}
        id={id}
        autoComplete="postal-code"
      />
    </>
  );
};

const FormInputRow = ({ children, header, sameLine, error, textColor }) => {
  const containerClassName = "row align-items-center my-1 form-input-row";
  const Header = () => (
    <div
      //style={sameLine && { paddingBottom: errorMsgSpacing }}
      className={`col-12 ${
        textColor ? "text-primary" : "text-dark"
      } fw-bold mb-md-0 fs-6 pb-1 ${
        (children && children.length) || !sameLine ? "ms-1" : "pb-md-3 col-md-6"
      }`}
    >
      {header}
    </div>
  );

  return (
    <>
      {(children && children.length) || !sameLine ? (
        <>
          {header && <Header />}
          <div className={containerClassName}>
            {children}
            {error && <div className="text-danger">{error}</div>}
          </div>
        </>
      ) : (
        <div className={containerClassName}>
          {header && <Header />}
          {children}
          {error && <div className="text-danger">{error}</div>}
        </div>
      )}
    </>
  );
};

const FormSection = ({ children, header, setPadding = true, align }) => {
  return (
    <div>
      {header && (
        <div className={`text-primary fw-bold fs-6 mb-2 ${align}`}>
          {header}
        </div>
      )}
      <div className={setPadding ? Styles.formSection : ""}>{children}</div>
    </div>
  );
};

const FormDetails = ({ header, content }) => {
  return (
    <div className="d-flex flex-column flex-sm-row align-items-start my-3 form-input-row gap-1 gap-md-5">
      <div className="text-dark fw-bold fs-6 col-sm-6">{header}</div>
      <div
        className="text-primary fw-bold fs-6 col-sm-6 text-wrap"
        style={{
          overflowWrap: "break-word",
          whiteSpace: "normal",
          minWidth: 0, // Helps text shrink properly in flex
        }}
      >
        {content}
      </div>
    </div>
  );
};

export {
  TextInput,
  RadioInput,
  FormInputRow,
  FormSection,
  DateInput,
  FormHeader,
  SelectInput,
  PhoneNumberInput,
  TextField,
  FormDetails,
  CheckboxInput,
  AddressInput,
};
